// /frontend/src/stores/ui.store.ts

/**
 * Pinia store for managing the global UI state.
 * This includes snackbars, dialogs, and loading indicators that
 * need to be triggered from anywhere in the application.
 */

import { defineStore } from 'pinia';

/** @interface ISnackbarState Defines the state for a snackbar message. */
export interface ISnackbarState {
  model: boolean;
  text: string;
  color: 'success' | 'error' | 'warning' | 'info';
  timeout: number;
}

/** @interface IUiState Defines the entire UI store's state. */
export interface IUiState {
  snackbar: ISnackbarState;
}

export const useUiStore = defineStore('ui', {
  /**
   * Defines the initial state of the UI store.
   *
   * @returns {IUiState} The initial state.
   *
   * @author Alexander Funke
   * @lastChange 2025-08-26
   */
  state: (): IUiState => ({
    snackbar: {
      model: false,
      text: '',
      color: 'info',
      timeout: 5000,
    },
  }),

  actions: {
    /**
     * Shows a global snackbar with a message.
     *
     * @param text The message to display.
     * @param color The color of the snackbar (defaults to 'error').
     *
     * @author Alexander Funke
     * @lastChange 2025-08-26
     */
    showSnackbar({ text, color = 'error' }: { text: string, color?: ISnackbarState['color'] }) {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.model = true;
    },
  },
});
